import { Fragment, useCallback, useEffect, useState } from "react";

import {
  createDeck,
  deleteDeck,
  listSetupDecks,
  renameDeck,
  reparentDecks,
  type DeckRow,
} from "@/lib/setup";
import { useCollectionChanged } from "@/lib/useCollectionChanged";
import { useDialogs } from "@/components/shell/Dialogs";
import { useMenu } from "@/components/shell/Menu";
import { usePhone } from "@/lib/usePhone";

import { DeckOptionsForm } from "./DeckOptions";
import { Panel, PanelButton } from "./Setup";

/** The tree is drawn by indentation: `padding-left: depth * 22px`, per the design. */
const INDENT_PX = 22;

export function DecksPanel({
  onError,
  onSuccess,
  onOpenDeckOptions,
}: {
  onError: (error: unknown) => void;
  onSuccess: () => void;
  /** The phone's drill-in (日本-344). On a desktop the form opens in place under its row. */
  onOpenDeckOptions?: (deckId: number) => void;
}) {
  const [decks, setDecks] = useState<DeckRow[]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | "root" | null>(null);
  /** The deck being renamed in place, by id. */
  const [renaming, setRenaming] = useState<number | null>(null);
  /** The deck whose options are open under its row — one at a time. Churn, not a place (日本-321). */
  const [optionsOpen, setOptionsOpen] = useState<number | null>(null);
  const [optionsChanged, setOptionsChanged] = useState(0);
  const dialogs = useDialogs();
  const isPhone = usePhone();
  const menu = useMenu();

  const load = useCallback(() => {
    void listSetupDecks()
      .then(({ decks: rows }) => setDecks(rows))
      .catch(onError);
  }, [onError]);

  useEffect(load, [load]);
  // Every count in this panel is a card count, and the jobs that move them run from the card above.
  // Without this the numbers sit at their pre-run values until you navigate away and back.
  useCollectionChanged(load);

  const run = async (action: () => Promise<{ decks: DeckRow[] } | unknown>) => {
    try {
      const result = (await action()) as { decks?: DeckRow[] };
      if (result?.decks) setDecks(result.decks);
      else load();
      onSuccess();
    } catch (e) {
      onError(e);
    }
  };

  const onCreate = async () => {
    const name = await dialogs.prompt({
      title: "新規デッキ",
      label: "デッキ名",
      hint: "サブデッキは :: で区切ります。例 Japanese::Names",
      submitLabel: "作成",
    });
    // Already trimmed by `promptValue`, which is also the fix for a deck created with a trailing
    // space — this validated `trim()` and then sent the raw string.
    if (name) void run(() => createDeck(name));
  };

  /**
   * Rename happens in the row, not in a dialog.
   *
   * A deck row is a table row wide enough to hold a field, and the library's log and folder rows
   * already rename this way — so a box asking for a name it could have shown you in place would be
   * the odd one out. The modal is kept for *creating* a deck, where there is no row yet and the
   * `::` hint has nowhere else to live.
   */
  const commitRename = (deck: DeckRow, raw: string) => {
    const name = raw.trim();
    setRenaming(null);
    if (name && name !== deck.name) void run(() => renameDeck(deck.id, name));
  };

  /**
   * Open, close or switch the options form. Leaving a form with unsaved changes asks first: a preset
   * edit is several fields' worth of typing, and the other row's `設定` sits right beside it.
   */
  const toggleOptions = async (deck: DeckRow) => {
    const next = optionsOpen === deck.id ? null : deck.id;
    if (optionsOpen !== null && optionsChanged > 0) {
      const current = decks.find((d) => d.id === optionsOpen);
      const ok = await dialogs.confirm({
        title: "変更を破棄しますか？",
        body: `${current ? current.name.split("::").pop() : ""}の変更 ${optionsChanged}件は保存されていません。`,
        confirmLabel: "破棄",
        tone: "danger",
      });
      if (!ok) return;
    }
    setOptionsChanged(0);
    setOptionsOpen(next);
  };

  const onDelete = async (deck: DeckRow) => {
    // Deleting a deck deletes its cards. Naming the count is the only thing that makes the
    // consequence visible before it happens.
    const cards = deck.total;
    const ok = await dialogs.confirm({
      title: `${deck.name} を削除？`,
      body:
        cards > 0
          ? "デッキを削除するとカードも消えます。取り消せません。"
          : "このデッキは空です。",
      detail: cards > 0 ? `${cards.toLocaleString()}枚` : undefined,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (ok) void run(() => deleteDeck(deck.id));
  };

  return (
    <Panel
      title="デッキ"
      subtitle={`${decks.length}デッキ`}
      actions={
        <PanelButton check="setup.new-deck" onClick={() => void onCreate()}>
          新規デッキ
        </PanelButton>
      }
    >
      {isPhone ? (
        decks.map((deck) => (
          <PhoneDeckRow
            key={deck.id}
            deck={deck}
            renaming={renaming === deck.id}
            onCommitRename={(value) => commitRename(deck, value)}
            onCancelRename={() => setRenaming(null)}
            onMenu={(trigger) =>
              menu.openFrom(
                trigger,
                [
                  ...(deck.hasSubdecks || !onOpenDeckOptions
                    ? []
                    : [
                        {
                          kind: "item" as const,
                          id: "options",
                          label: "デッキ設定",
                          onSelect: () => onOpenDeckOptions(deck.id),
                        },
                      ]),
                  {
                    kind: "item",
                    id: "rename",
                    label: "名前を変更",
                    onSelect: () => setRenaming(deck.id),
                  },
                  {
                    kind: "item",
                    id: "delete",
                    label: "削除",
                    danger: true,
                    onSelect: () => void onDelete(deck),
                  },
                ],
                `deck-${deck.id}`,
                "end",
              )
            }
          />
        ))
      ) : (
        <>
      <div className="flex items-center gap-3 border-b border-line-soft px-[18px] py-[6px] text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
        <span className="flex-1">デッキ</span>
        <span className="w-[52px] text-right">新規</span>
        <span className="w-[52px] text-right">要復習</span>
        <span className="w-[64px] text-right">合計</span>
        <span
          className="w-[52px] text-right"
          title="今日スケジューラが出す枚数。上限を適用し、親が子より優先、サブデッキを含みます"
        >
          今日
        </span>
        <span className="w-[76px] text-right">上限</span>
        <span className="w-[230px]" />
      </div>

      {/* Dropping onto the header row moves a deck to the top level — the only way back out of a
          nesting, since there is no free ordering to drag within. */}
      <div
        onDragOver={(e) => {
          if (dragging === null) return;
          e.preventDefault();
          setDropTarget("root");
        }}
        onDrop={() => {
          if (dragging !== null) void run(() => reparentDecks([dragging], null));
          setDragging(null);
          setDropTarget(null);
        }}
        className={[
          "border-b border-line-soft px-[18px] py-1 text-meta",
          dropTarget === "root" ? "bg-accent-tint text-accent-text" : "text-ink-4",
        ].join(" ")}
      >
        {dragging === null ? "デッキを別のデッキに重ねると入れ子になります" : "ここに落とすと最上位へ"}
      </div>

      {decks.map((deck) => {
        const leaf = deck.name.split("::").pop() ?? deck.name;
        const open = optionsOpen === deck.id;
        return (
          <Fragment key={deck.id}>
          <div
            // Not while renaming, or dragging to select the text inside the field starts a deck
            // drag instead — the guard `TabStrip` learned the same way.
            draggable={renaming !== deck.id}
            onDragStart={() => setDragging(deck.id)}
            onDragEnd={() => {
              setDragging(null);
              setDropTarget(null);
            }}
            onDragOver={(e) => {
              if (dragging === null || dragging === deck.id) return;
              e.preventDefault();
              setDropTarget(deck.id);
            }}
            onDrop={() => {
              if (dragging !== null && dragging !== deck.id) {
                void run(() => reparentDecks([dragging], deck.id));
              }
              setDragging(null);
              setDropTarget(null);
            }}
            className={[
              "flex items-center gap-3 border-b px-[18px] py-[9px]",
              open ? "border-line bg-raised" : "border-line-soft",
              dropTarget === deck.id ? "bg-accent-tint" : "",
              dragging === deck.id ? "opacity-50" : "",
            ].join(" ")}
          >
            <span
              aria-hidden
              title="ドラッグで入れ子に"
              className="cursor-grab select-none font-mono text-[15px] text-ink-4"
            >
              ⠿
            </span>
            {renaming === deck.id ? (
              <input
                autoFocus
                // Selects what is there, like every other rename in the app: the field opens on
                // the old name, so without this renaming means typing and then backspacing over
                // what you came to replace.
                onFocus={(event) => event.currentTarget.select()}
                // **The FULL name, not `leaf`.** `renameDeck` takes the whole `::` path, so a
                // field seeded with the leaf would rename `Japanese::Names` to `Names` and move it
                // to the top level — and the row displays the leaf, so it would look right until
                // the list reloaded. Editing the path is also how a deck is re-parented by hand.
                defaultValue={deck.name}
                onKeyDown={(event) => {
                  // A deck name is routinely Japanese (`Japanese::漢字`), and an IME commits its
                  // conversion with Enter — so without this the rename lands on the pre-conversion
                  // text, across the whole collection. `isComposing` plus keyCode 229 for the
                  // older WebView the desktop shell is, exactly as `AskPanel` does.
                  if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                  if (event.key === "Enter") commitRename(deck, event.currentTarget.value);
                  else if (event.key === "Escape") setRenaming(null);
                }}
                onBlur={(event) => commitRename(deck, event.currentTarget.value)}
                // No indent: the field holds the whole path, so indenting it as well would say the
                // depth twice.
                className="min-w-0 flex-1 rounded-[var(--r-2)] border border-line-strong bg-void px-[6px] py-[2px] text-body text-ink"
              />
            ) : (
              <span
                // The same hook the phone row carries, and it must be on BOTH — the width
                // assertion in `check-mobile.mjs` is about whether a deck name has room to be
                // read, and the failure it exists for is *this* markup rendering at 390px. On the
                // phone branch alone the check found nothing to measure and passed vacuously,
                // which is how it behaved when it was first written.
                data-check="setup.deck-name"
                className="min-w-0 flex-1 truncate text-body text-ink"
                style={{ paddingLeft: deck.depth * INDENT_PX }}
                title={deck.name}
              >
                {leaf}
              </span>
            )}
            <span className="w-[52px] text-right font-mono text-secondary text-[color:var(--accent)]">
              {deck.new || ""}
            </span>
            <span className="w-[52px] text-right font-mono text-secondary text-ink-3">
              {deck.due || ""}
            </span>
            <span className="w-[64px] text-right font-mono text-secondary text-ink-2">
              {deck.total.toLocaleString()}
            </span>
            {/* The raw counts left of this are administration — how big the deck is. This one is
                the study answer, and it is what the rail's badge shows. */}
            <span className="w-[52px] text-right font-mono text-secondary text-[color:var(--accent)]">
              {deck.dueToday || ""}
            </span>
            <span
              data-check="setup.deck-limits"
              className="w-[76px] whitespace-nowrap text-right font-mono text-meta text-ink-4"
            >
              {deck.newLimit} · {deck.reviewLimit}
            </span>
            <span className="flex w-[230px] justify-end gap-1.5">
              {/* Not on a parent: its limits are layered over every child (`_capped_at3`), so
                  editing them changes what every child shows (日本-344). */}
              {!deck.hasSubdecks && (
                <RowAction
                  check="setup.deck-options"
                  active={open}
                  expanded={open}
                  onClick={() => void toggleOptions(deck)}
                >
                  {open ? "設定 ▴" : "設定"}
                </RowAction>
              )}
              <RowAction onClick={() => setRenaming(deck.id)}>名前を変更</RowAction>
              <RowAction onClick={() => void onDelete(deck)} danger>
                削除
              </RowAction>
            </span>
          </div>
          {open && (
            <DeckOptionsForm
              deckId={deck.id}
              layout="inline"
              onDirtyChange={setOptionsChanged}
              onSaved={(rows) => {
                setDecks(rows);
                onSuccess();
              }}
            />
          )}
          </Fragment>
        );
      })}
        </>
      )}
    </Panel>
  );
}

/**
 * A deck on a phone: two lines and a `⋯`, where the desktop has seven columns.
 *
 * The desktop row's fixed columns come to 464px before the name has any width at all, so at 390px
 * the name's `flex-1 min-w-0` resolved to **zero** and every deck in the panel drew as an empty
 * row with numbers beside it. Nothing overflowed and nothing scrolled sideways, so the standing
 * horizontal-scroll assertion could not see it — the same shape as the phone's two-image row.
 *
 * What the second line keeps is what the Mobile canvas's 設定 frame keeps: how big the deck is, and
 * how much of it is waiting. The per-deck limits are administration you go to a desktop for, and
 * `New`/`Today` are recoverable from the study card on 復習's landing, which is where you would act
 * on them anyway.
 *
 * **Rename and Delete move into the `⋯` rather than shrinking.** Two 28px buttons on every row of a
 * 390px screen is 40% of the width spent on the two actions you take least often, and Delete is a
 * hard delete of every card in the deck sitting under a thumb scrolling a list.
 */
function PhoneDeckRow({
  deck,
  renaming,
  onCommitRename,
  onCancelRename,
  onMenu,
}: {
  deck: DeckRow;
  renaming: boolean;
  onCommitRename: (value: string) => void;
  onCancelRename: () => void;
  onMenu: (trigger: HTMLElement) => void;
}) {
  const leaf = deck.name.split("::").pop() ?? deck.name;
  return (
    <div
      data-check="setup.deck-row"
      className="flex items-center gap-[10px] border-b border-line-soft px-[16px] py-[9px]"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        {renaming ? (
          <input
            autoFocus
            onFocus={(event) => event.currentTarget.select()}
            defaultValue={deck.name}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing || event.keyCode === 229) return;
              if (event.key === "Enter") onCommitRename(event.currentTarget.value);
              else if (event.key === "Escape") onCancelRename();
            }}
            onBlur={(event) => onCommitRename(event.currentTarget.value)}
            className="min-w-0 rounded-[var(--r-2)] border border-line-strong bg-void px-[6px] py-[3px] text-body text-ink"
          />
        ) : (
          <span
            data-check="setup.deck-name"
            className="truncate text-body text-ink"
            style={{ paddingLeft: deck.depth * 12 }}
            title={deck.name}
          >
            {leaf}
          </span>
        )}
        <span
          className="truncate font-mono text-meta text-ink-3"
          style={{ paddingLeft: renaming ? 0 : deck.depth * 12 }}
        >
          {deck.total.toLocaleString()}枚
          {deck.dueToday > 0 && (
            <>
              {" · "}
              <span className="text-[color:var(--accent)]">要復習{deck.dueToday}</span>
            </>
          )}
        </span>
      </div>
      <button
        type="button"
        aria-label={`${deck.name} の操作`}
        onClick={(event) => onMenu(event.currentTarget)}
        className="-mr-[10px] flex h-11 w-11 shrink-0 items-center justify-center text-[17px]/[1] text-ink-3"
      >
        ⋯
      </button>
    </div>
  );
}

function RowAction({
  onClick,
  danger,
  active,
  expanded,
  check,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  /** The row's own form is open under it (日本-344). */
  active?: boolean;
  expanded?: boolean;
  check?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      data-check={check}
      className={[
        "rounded-[var(--r-2)] border px-[9px] py-[5px] text-[14px]/[1] font-semibold",
        danger
          ? "border-accent-line bg-accent-tint text-accent-hi"
          : active
            ? "border-line-strong bg-raised text-ink"
            : "border-line bg-void text-ink-2",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
